// Step 1 — Basics. Three controls stacked: campaign name + channels + offer.
// Switching channels/offer when later-step content has been authored prompts
// a destructive-change confirmation (UX doc §6.5).

import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Icon, Textinput, Typo, Modal } from '../../../../ui';
import { CHANNEL, CHANNELS, OFFER, OFFERS } from '../../constants';
import { cleanForChannels, cleanForOffer } from '../../utils/draftDefaults';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Group = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const GroupLabel = styled.legend`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.NEU_PRIMARY};
`;

const PickerGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const PickCard = styled.button`
  flex: 1 1 240px;
  text-align: left;
  background: ${({ $selected, theme }) => ($selected ? theme.colors.PRIMARY_LIGHT : theme.colors.NEU_0)};
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.colors.PRIMARY : theme.colors.NEU_200)};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: border-color 0.15s, background 0.15s;

  &:hover { border-color: ${({ theme }) => theme.colors.PRIMARY}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.PRIMARY}; outline-offset: 2px; }
`;

const PickHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.NEU_PRIMARY};
`;

const PickIcon = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.NEU_50};
  color: ${({ theme }) => theme.colors.NEU_PRIMARY};
`;

const PickBody = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.NEU_600};
  line-height: 1.4;
`;

const InfoRibbon = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: ${({ theme }) => theme.colors.NEU_50};
  color: ${({ theme }) => theme.colors.NEU_600};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.NEU_600};
  margin-top: 4px;
`;

const FieldError = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.ERROR_RED};
  margin-top: 4px;
`;

const CHANNEL_META = {
  [CHANNEL.EMAIL]: {
    icon: <Icon.Email size='small' />,
    title: 'Email',
    body: 'Deliver via Mailchimp to each student\'s inbox. Best for newsletters, promos and announcements.',
  },
  [CHANNEL.NETWORK_UPSELL]: {
    icon: <Icon.TrendingUp size='small' />,
    title: 'In-app recommendation',
    body: 'Show a course card in the Coursio Network placement on the student dashboard.',
  },
};

const OFFER_META = {
  [OFFER.NONE]: { icon: <Icon.Info size='small' />, title: 'No offer', body: 'Plain marketing content. No code, no gift.' },
  [OFFER.INVITATION]: { icon: <Icon.Gift size='small' />, title: 'Course invitation', body: 'Grant course access via a gift code with an expiry.' },
  [OFFER.DISCOUNT]: { icon: <Icon.Coupon size='small' />, title: 'Discount code', body: '% off or fixed amount, with a validity window.' },
};

// Detect destructive scope of a channel/offer change so the user can confirm.
const channelChangeImpact = (currentChannels, nextChannels, draft) => {
  const lost = [];
  if (currentChannels.includes(CHANNEL.EMAIL) && !nextChannels.includes(CHANNEL.EMAIL)) {
    const v = draft?.content?.variants?.[0];
    if (v?.subject || v?.body) lost.push('email subject, body and CTA');
  }
  if (currentChannels.includes(CHANNEL.NETWORK_UPSELL) && !nextChannels.includes(CHANNEL.NETWORK_UPSELL)) {
    const f = draft?.content?.networkUpsell?.featuredCourseIds ?? [];
    if (f.length > 0) lost.push('featured courses');
  }
  return lost;
};

const offerChangeImpact = (currentOffer, nextOffer, draft) => {
  const lost = [];
  if (currentOffer === OFFER.DISCOUNT && nextOffer !== OFFER.DISCOUNT) {
    if (draft?.discount?.code || draft?.discount?.validUntil) lost.push('discount code and validity');
  }
  if (currentOffer === OFFER.INVITATION && nextOffer !== OFFER.INVITATION) {
    if (draft?.invitation?.courseId || draft?.invitation?.expiresAt) lost.push('invitation course and expiry');
  }
  return lost;
};

const StepBasics = ({ draft, errors = {}, onChange }) => {
  const { t } = useTranslation();
  const [pendingDestructive, setPendingDestructive] = useState(null);

  const channels = draft?.channels ?? [];
  const offer = draft?.offer ?? OFFER.NONE;

  const setChannels = useCallback((nextChannels) => {
    const lost = channelChangeImpact(channels, nextChannels, draft);
    if (lost.length > 0) {
      setPendingDestructive({ kind: 'channels', next: nextChannels, lost });
      return;
    }
    onChange(cleanForChannels(draft, nextChannels));
  }, [channels, draft, onChange]);

  const setOffer = useCallback((nextOffer) => {
    const lost = offerChangeImpact(offer, nextOffer, draft);
    if (lost.length > 0) {
      setPendingDestructive({ kind: 'offer', next: nextOffer, lost });
      return;
    }
    onChange(cleanForOffer(draft, nextOffer));
  }, [offer, draft, onChange]);

  const confirmDestructive = useCallback(() => {
    if (!pendingDestructive) return;
    if (pendingDestructive.kind === 'channels') onChange(cleanForChannels(draft, pendingDestructive.next));
    if (pendingDestructive.kind === 'offer') onChange(cleanForOffer(draft, pendingDestructive.next));
    setPendingDestructive(null);
  }, [pendingDestructive, draft, onChange]);

  const toggleChannel = (channel) => {
    if (channels.includes(channel)) {
      if (channels.length === 1) return; // can't deselect last
      setChannels(channels.filter((c) => c !== channel));
    } else {
      setChannels([...channels, channel]);
    }
  };

  return (
    <>
      <Card>
        <Header>
          <Typo.ContentSmallHeader>{t('campaigns.basics.title', 'Campaign basics')}</Typo.ContentSmallHeader>
          <Typo.SmallText color='textSecondary'>
            {t(
              'campaigns.basics.description',
              'Name your campaign, pick how it reaches students, and (optionally) attach an offer. Channels and offer compose freely — you can do email only, in-app only, or both.',
            )}
          </Typo.SmallText>
        </Header>

        <Group>
          <GroupLabel>{t('campaigns.basics.name', 'Campaign name')}</GroupLabel>
          <Textinput
            value={draft?.name ?? ''}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            placeholder={t('campaigns.basics.namePlaceholder', 'e.g. Summer Sale 2026')}
          />
          {errors.name ? <FieldError>{errors.name}</FieldError> : <Hint>{t('campaigns.basics.nameHint', 'Visible only to you and your team. Students never see this name.')}</Hint>}
        </Group>

        <Group>
          <GroupLabel>{t('campaigns.basics.channels', 'Channels')}<Hint as='span' style={{ marginLeft: 8, fontWeight: 400 }}>· {t('campaigns.basics.channelsHint', 'select one or both')}</Hint></GroupLabel>
          <PickerGrid>
            {CHANNELS.map((channel) => {
              const meta = CHANNEL_META[channel];
              const selected = channels.includes(channel);
              return (
                <PickCard key={channel} $selected={selected} onClick={() => toggleChannel(channel)} type='button' aria-pressed={selected}>
                  <PickHeader><PickIcon>{meta.icon}</PickIcon>{t(`campaigns.channelTitle.${channel}`, meta.title)}</PickHeader>
                  <PickBody>{t(`campaigns.channelBody.${channel}`, meta.body)}</PickBody>
                </PickCard>
              );
            })}
          </PickerGrid>
          {errors.channels && <FieldError>{errors.channels}</FieldError>}
          {channels.length === 2 && (
            <Hint>{t('campaigns.basics.bothChannelsHint', 'Audience will receive both surfaces. You can tune the timing in step 4.')}</Hint>
          )}
        </Group>

        <Group>
          <GroupLabel>{t('campaigns.basics.offer', 'Offer')}<Hint as='span' style={{ marginLeft: 8, fontWeight: 400 }}>· {t('campaigns.basics.offerHint', 'optional payload attached to the campaign')}</Hint></GroupLabel>
          <PickerGrid>
            {OFFERS.map((o) => {
              const meta = OFFER_META[o];
              const selected = offer === o;
              return (
                <PickCard key={o} $selected={selected} onClick={() => setOffer(o)} type='button' aria-pressed={selected}>
                  <PickHeader><PickIcon>{meta.icon}</PickIcon>{t(`campaigns.offerTitle.${o}`, meta.title)}</PickHeader>
                  <PickBody>{t(`campaigns.offerBody.${o}`, meta.body)}</PickBody>
                </PickCard>
              );
            })}
          </PickerGrid>
          {errors.offer && <FieldError>{errors.offer}</FieldError>}
        </Group>

        {channels.length === 2 && offer === OFFER.DISCOUNT && (
          <InfoRibbon>
            <Icon.Info size='small' />
            <span>
              <strong>{t('campaigns.basics.blendedLaunch', 'Blended launch:')}</strong>{' '}
              {t('campaigns.basics.blendedLaunchBody', 'both channels + a discount offer. The email body will include the code, and the in-app card will show a matching badge.')}
            </span>
          </InfoRibbon>
        )}
      </Card>

      <Modal
        open={!!pendingDestructive}
        onClose={() => setPendingDestructive(null)}
        title={t('campaigns.basics.destructiveTitle', 'Switching will clear data')}
        showActions
        onAccept={confirmDestructive}
        acceptLabel={t('globals.continue', 'Continue')}
        variant='action'
      >
        <Typo.MediumText>
          {t('campaigns.basics.destructiveBody', 'This change will clear: {{fields}}. You can cancel to keep what you have.', {
            fields: pendingDestructive?.lost?.join(', ') ?? '',
          })}
        </Typo.MediumText>
      </Modal>
    </>
  );
};

export default StepBasics;
